<template>
  <div class="item" v-bind:class="{'wide-view': wideView, 'shared': podcast.shared}">
    <button v-if="!displayPlayerInline" class="open-futuri-media" v-bind:id="`show-futuri-player-${index}`" v-on:click.prevent="openMedia">Open</button>
    <input v-if="displayPlayerInline"
           type="radio"
           name="play-futuri-media"
           class="play-futuri-media"
           v-bind:id="`show-futuri-player-${index}`"
           v-on:click="playMedia"
           ref="activator" />
    <label class="show-futuri-player" v-bind:for="`show-futuri-player-${index}`">
      <div class="media-player-container" v-bind:class="{ 'art-settled': artSettled }">
        <img class="item-image"
             v-if="podcast.image"
             v-bind:src="podcast.image"
             v-bind:alt="podcast.name"
             v-on:load="imageLoaded"
             v-on:error="imageFailed">
        <div v-else class="item-image item-image-placeholder"></div>
        <media-player
          v-if="mediaReady"
          v-bind:index="index"
          v-bind:id="podcast.id"
          v-bind:station="podcast.station"
          v-bind:duration="duration"
          v-bind:drop-to-live="dropToLive"
          v-bind:is-playing="isPlaying"
          v-bind:seek-position="seekPos"
          v-bind:time-left="timeLeft"
          v-bind:playing-live-stream="canPlayLiveStream"
          v-on:play="playMedia"
          v-on:pause="pauseMedia"
          v-on:seeking="onSeeking"
        ></media-player>
        <audio v-bind:id="`audio-player-${index}`"
               class="video-player"
               ref="player"
               v-on:timeupdate="onTimeUpdate"
               v-on:canplay="onCanPlay"
               v-on:play="onPlay"
               v-on:pause="onPause"
               v-on:ended="onEnded"
               v-on:error="onMediaError"
        >
          <source :src="audioSrc" @error="onMediaError" />
        </audio>
      </div>
      <h2>{{podcast.name}}</h2>
      <p class="description">{{podcast.description}}</p>
    </label>
    <ul v-if="itemCategories.length > 0" class="categories">
      <li v-for="category in itemCategories" :key="category.id">
        <a v-on:click.prevent="clickCategory(category)">{{category.name}}</a>
      </li>
    </ul>
  </div>
</template>

<script>
// 🔴 D-146 — the NotAllowed measurement was v4-ONLY, so a blocked autoplay on every
// other version was invisible in Faro. NotAllowedError is the browser refusing to play
// without a gesture: exactly the failure a listener experiences as "the widget is
// broken" and an operator cannot otherwise see. F-3/MR-69 chose a per-call-site report
// over a global `unhandledrejection` listener PRECISELY because each catch knows its own
// station — and then only one version's call sites got it. Ninth one-of-N here.
//
// `reportNotAllowedIfApplicable` filters on `isNotAllowedRejection` and never throws, so
// it is safe on every rejection path, including the benign AbortError ones.
import { reportNotAllowedIfApplicable } from '../core/faro';
import MediaPlayer from './media_player';
import { getLiveStreamUrl, buildPlayerUrl } from '../utils/utils';
// LP-4 / MR-78: live audio routes through the shared hls.js engine (LP-H/MR-75)
// instead of the retired Viblast runtime global.
import { attachLiveStream, detachLiveStream } from '../utils/hls_engine';

export default {
  components: {
    'media-player': MediaPlayer,
  },
  // Vue3: declare the event so `@pauseothers` on <podcast> is a real component
  // event, not an undeclared listener that falls through as a native DOM attr
  // (Vue1 had no emits declaration; the fan-out chain is unchanged).
  emits: ['pauseothers'],
  props: [
    'displayPlayerInline',
    'dropToLive',
    'containerWidth',
    'index',
    'podcast',
    'smc',
    'useM4a',
    'apiPrefix',
  ],
  data() {
    return {
      isPlaying: false,
      seekPos: 0,
      duration: 0,
      timeLeft: 0,
      seekTime: null,
      mediaReady: false,
      // Whether the cover art has reported `load`. Drives the `art-settled` class that
      // RELEASES the pre-load aspect-ratio reservation — see the .item-image styles.
      artSettled: false,
      canPlayLiveStream: false,
      liveStreamUri: '',
      preventAudioSrc: true,
    };
  },
  methods: {
    openMedia() {
      window.open(this.playerUrl, '_blank', 'status=yes, resizable=yes');
    },

    playMedia() {
      this.preventAudioSrc = false;
      this.pauseOthers();
      const audioPlayer = this.$refs.player;
      if (this.canPlayLiveStream && !this.isPlaying) {
        // if live stream paused and play is clicked, original playlist should start again
        this.canPlayLiveStream = false;
      }

      this.updatePlayerElementIfNecessary();

      // Now is this one already playing?
      if (this.isPlaying) {
        this.pauseMedia();
      } else {
        audioPlayer.play()
          // eslint-disable-next-line no-console
          // 🔴 D-176 — `.then(null, handler)`, not `.catch(handler)`: a host page that patches
          // `play()` (ad blockers, some CMPs) can return a THENABLE with no `.catch`, and
          // calling it throws TypeError at the moment playback already failed. `.then` is the
          // only method a thenable must have. Same contract as startPlayback in hls_engine.js.
          .then(null, (err) => {
            reportNotAllowedIfApplicable(err, this.podcast && this.podcast.station);
            console.error('Play failed', err);
          });
      }
    },

    updatePlayerElementIfNecessary() {
      const audioPlayer = this.$refs.player;
      if (this.useM4a) {
        // m4a bypass stays NATIVE — hls.js must never attach for m4a (the old
        // note: "Viblast doesn't support m4a playback"). detachLiveStream is
        // idempotent + throw-safe (was the guarded viblast(el).stop()).
        // 🔑 Compare against the DESIRED src, exactly as the live branch below does
        // ("the compare is exact"). This used to ask only whether the current src
        // LOOKED like an m4a — `!src.includes('m4a')` — which is true of the previous
        // episode's m4a too, so a REUSED element was never re-pointed.
        //
        // Reuse is not hypothetical on Vue 3: `podcast_list.vue` carries
        // `:key="item.id"`, that line is identical on master, and Vue 1 ignored :key
        // while Vue 3 honours it. So a page or category refetch now hands an existing
        // <podcast> a new podcast prop and the SAME <audio>. With colliding ids across
        // stations that served station A's audio under station B's title (Phase 2).
        //
        // getAttribute, not `.src`: the property returns the URL resolved against the
        // document base, so comparing it to a possibly-relative audioSrc would differ
        // every time and re-point on every call, restarting playback.
        const currentSrc = audioPlayer.getAttribute('src');
        if (!currentSrc || (!this.canPlayLiveStream && currentSrc !== this.audioSrc)) {
          detachLiveStream(audioPlayer);
          audioPlayer.setAttribute('src', this.audioSrc);
        }
      } else {
        // Live HLS via the shared engine (LP-H/MR-75). attachLiveStream records
        // the attached URL on the element (`dataset.hlsSrc` = EXACTLY this.audioSrc,
        // cleared by detach), replacing viblast's `el.viblast` / `dataset.viblastSrc`
        // bookkeeping. Because the engine stores the raw url, the old
        // `.split(':').pop()` protocol-strip (which normalized viblast's own stored
        // value) is gone — the compare is exact.
        const attachedSrc = audioPlayer.dataset.hlsSrc;
        if (!attachedSrc || (!this.canPlayLiveStream && attachedSrc !== this.audioSrc)) {
          this.setupHlsEngine();
        }
      }
    },

    pauseMedia() {
      if (this.$refs.player) {
        this.$refs.player.pause();
      }
    },

    pauseOthers() {
      this.$emit('pauseothers');
    },

    onSeeking(pos) {
      // Update the video time
      this.$refs.player.pause();
      this.seekTime = this.$refs.player.duration * (pos / 100);
      this.$refs.player.currentTime = this.seekTime;
      // 🔴 D-176 — `.then(null, handler)`, not `.catch(handler)`. A host page that
      // patches HTMLMediaElement.prototype.play (ad blockers and some CMPs do) can hand
      // back a THENABLE with no `.catch`, and `.catch(h)` on it throws TypeError —
      // turning a handled rejection into an unhandled one at the exact moment playback
      // failed. `.then(null, h)` needs only `.then`, which any thenable has by
      // definition. Applied at hls_engine's startPlayback first and only there; this is
      // the same contract, one of the sites it missed.
      this.$refs.player.play().then(null, (err) => {
        // Error is being thrown because pause is interrupting the play at times.
        // D-146: reported anyway — the helper filters to NotAllowedError, so a genuine
        // autoplay block here stops being swallowed alongside the benign interrupt.
        reportNotAllowedIfApplicable(err, this.podcast && this.podcast.station);
      });
    },

    clickCategory(cat) {
      this.$parent.switchCategory(cat);
    },

    imageLoaded() {
      // Release the box reservation FIRST: from here the image's own intrinsic ratio
      // governs, so the reserved 16/9 can never distort what the visitor actually sees.
      //
      // 🔑 ORDERING IS LOAD-BEARING, and it is PINNED — `podcast-art-reservation.spec.js`
      // drives this handler with a parent hook that THROWS and requires `art-settled` to be
      // applied anyway. Moving this line below the forward passes every other test in the
      // file, and would mean a parent that throws leaves the cover box reserved forever.
      this.artSettled = true;
      // 🔴 THE try/catch IS THE GUARD, AND IT IS THE WHOLE GUARD. This reaches ACROSS to the
      // parent chain from an image `load` handler on a customer's page, and an unguarded
      // cross-component reach on an observability path is the one shape this codebase rules
      // out everywhere else — core/telemetry.js, core/faro.js and app.vue's own parentOrigin
      // block all say observability must never surface to the embed.
      //
      // 🪤 IT USED TO ALSO TEST `typeof this.$parent.imageLoaded === 'function'`, AND THAT
      // CHECK COULD NOT FAIL (review item S13). `<podcast>` is rendered from exactly one
      // place — `podcast_list.vue`'s v-for — and podcast_list always defines `imageLoaded`,
      // so the condition was structurally true and read as protection nobody had. What the
      // catch below genuinely covers is the REST of the chain: podcast_list forwards bare to
      // app.vue, whose `imageLoaded` calls `informResize`, which measures the DOM and
      // postMessages the host. Any of that can throw; none of it may reach the embed.
      try {
        this.$parent.imageLoaded();
      } catch (e) {
        // 🪤 NOT "telemetry". What this wraps is `_artworkSignalled` (telemetry) AND the
        // resize handshake `postMessage` (not telemetry at all — it is how the iframe gets
        // its height). Naming only the first would let a future reader conclude a swallowed
        // error here is harmless.
        /* the layout beacon and the resize handshake must never surface to the embed */
      }
    },

    // 🔴 A COVER THAT NEVER ARRIVES MUST RELEASE THE BOX TOO, and until this existed
    // nothing did: `art-settled` had exactly one writer, in `imageLoaded()`, and the `<img>`
    // bound only `@load`. MEASURED on real builds with the covers 404ing — `.item-image`
    // 700x394, naturalWidth 0, `aspect-ratio: 16/9` still applied, h2 pushed to top 406,
    // iframe 1279, byte-identical 10s later, against 16x16 / top 28 / 369 before the
    // reservation existed. `route.abort('blockedbyclient')` reproduces it, so an ad-blocker
    // is enough. All three sibling players already bind `@error` (v2 NowPlayingImage.vue,
    // v3 Player.vue, v4 Player.vue); v1 was the one that did not.
    //
    // 🔑 DELIBERATELY NOT `imageLoaded`. That hop sets app.vue's `_artworkSignalled`, which
    // is what stamps `artwork_settled="true"` on the widget_layout beacon — the flag a
    // querier uses to decide whether an `artwork_h` reading is a real measurement or a
    // give-up. Forwarding here would certify the measurement of a cover that never arrived
    // and poison the artwork distribution the CSS block below cites.
    //
    // It DOES have to reach the shell, though: releasing the reservation removes ~910px of
    // box, and without a re-post the embedder keeps the tall iframe — the same blank frame
    // this fix exists to remove, relocated below the content. Mirrors v4's `imageError()`
    // → `notifyShellOfResize()`. Guarded and caught for the same reason `imageLoaded` is:
    // this runs from an image event handler on a customer's page.
    //
    // 🪤 THE `alt` ABOVE IS NOT A SUBSTITUTE FOR THIS, though it looks like one. MEASURED in
    // the pixel harness: with `alt` present but this handler removed, the failed-cover
    // layout is IDENTICAL (27/27 cells pass), because Chromium renders a broken image that
    // has alt text as an inline alt-text run rather than a replaced element — so
    // `width:100%` + `aspect-ratio` build no box and it collapses on its own. That is one
    // engine's rendering of a broken image, not a release of the reservation; the class is
    // what makes it explicit and engine-independent. `podcast-art-reservation.spec.js` is
    // what holds this handler in place — the pixel cell cannot see it.
    imageFailed() {
      // Same ordering rule, same reason, same pin: the release must not be reachable only
      // through a forward that can throw.
      this.artSettled = true;
      try {
        this.$parent.imageFailed();
      } catch (e) {
        /* the resize handshake must never surface to the embed — see imageLoaded */
      }
    },

    playLiveStream() {
      getLiveStreamUrl(this.podcast.station)
        .then((url) => {
          this.liveStreamUri = url;
          this.canPlayLiveStream = true;
          // 🔴 D-153, THIRD CALLER — the fix landed on v3 and v4 and missed v1.
          //
          // The guard's own justification on v3/v4 states the defect universally
          // ("Every caller does `attachLiveStream(this.$refs.player, ...)` inside a
          // `getLiveStreamUrl(...).then(...)`"), and it was true — there are three
          // such callers and only two were fixed. Found by an adversarial review
          // round, in which FOUR independent lenses reached this line.
          //
          // Without it: the listener navigates or the host tears the iframe down
          // while the two-fetch `getLiveStreamUrl` chain is in flight, Vue nulls the
          // template ref, this `.then` throws `TypeError: Cannot read properties of
          // null (reading 'play')`, and the chained `.catch` below logs
          // "Unable to fetch live stream" — for a fetch that returned 200. Anyone
          // triaging live-audio complaints goes hunting a CDN that answered fine.
          // REPRODUCED on this component before the fix; the same probe on v3/v4
          // passes because of the guard this mirrors.
          //
          // 🔑 The population is now DERIVED rather than hand-listed — see
          // `test/unit-vue/live-fetch-unmount-population.spec.js`. A two-member
          // `describe.each` is what let this survive, and it is the same one-of-N
          // shape this work has hit repeatedly.
          const player = this.$refs.player;
          if (!player) return;
          this.setupHlsEngine();
          player.play()
            // eslint-disable-next-line no-console
            // 🔴 D-176 — `.then(null, handler)`, not `.catch(handler)`: a host page that patches
            // `play()` (ad blockers, some CMPs) can return a THENABLE with no `.catch`, and
            // calling it throws TypeError at the moment playback already failed. `.then` is the
            // only method a thenable must have. Same contract as startPlayback in hls_engine.js.
            .then(null, (err) => {
              reportNotAllowedIfApplicable(err, this.podcast && this.podcast.station);
              console.warn('Play call interrupted', err);
            });
        })
        // eslint-disable-next-line no-console
        .catch(e => { console.warn('Unable to fetch live stream', e); });
    },

    setupHlsEngine() {
      // Attach the live HLS stream via the shared hls.js engine (LP-H/MR-75),
      // replacing the old Viblast stop()+setup() calls. attachLiveStream does its
      // own stop-before-attach (it tears down any prior session first, as the old
      // viblast(el).stop() always preceded setup()). autoplay:false keeps the
      // Viblast contract — playMedia / playLiveStream call $refs.player.play()
      // explicitly. The `key` (Viblast license) + `forceHEAAC` drop — no hls.js
      // equivalent (HE-AAC is handled by hls.js's transmuxer). attachLiveStream is
      // already throw-safe (it swallows the iOS quirk the old setup was try/caught
      // for); the try/catch is kept for belt-and-suspenders parity.
      try {
        attachLiveStream(this.$refs.player, this.audioSrc, { autoplay: false });
      } catch (e) {
        // swallowed — engine is throw-safe; see method note
      }
    },

    onTimeUpdate(event) {
      // Update the slider value to either new seek tir currentTime
      this.seekPos = (100 / (event.target.duration || 1)) * event.target.currentTime;
      this.timeLeft = (event.target.duration || 0) - event.target.currentTime;
    },

    onCanPlay(event) {
      this.duration = event.target.duration;
      this.mediaReady = true;
    },

    /**
     * D-133 — v1 had NO error binding on either element, so a failed load left
     * `mediaReady` true from whatever episode loaded last: the component went on
     * believing it had playable media.
     *
     * Milder than v2's wedge on purpose — `playMedia` calls `play()` with a
     * `.catch`, so the button here was already retryable, just silent. This makes
     * the failure observable without changing the retry behaviour.
     *
     * Bound on BOTH elements: a `<source>` failure does not bubble to the media
     * element, which is the engine fact D-105 measured.
     */
    onMediaError() {
      this.mediaReady = false;
    },

    onPlay() {
      this.isPlaying = true;
    },

    onPause() {
      this.isPlaying = false;
    },

    onEnded() {
      this.isPlaying = false;
      if (this.dropToLive) {
        this.playLiveStream();
      }
    },
  },
  computed: {
    m3u8URI() {
      return `${this.podcastUrl}/playlist/${this.podcast.id}.m3u8`;
    },

    m4aURI() {
      return `${this.apiPrefix}${this.podcast.m4aFile}`;
    },

    audioSrc() {
      if (this.preventAudioSrc) {
        return undefined;
      }
      if (!this.canPlayLiveStream) {
        return this.useM4a ? this.m4aURI : this.m3u8URI;
      }
      return this.liveStreamUri;
    },

    wideView() {
      return this.containerWidth >= 590;
    },

    proto() {
      const proto = document.location.protocol;
      if (proto === 'file:') {
        return 'http:';
      }
      return proto;
    },

    slug() {
      return this.podcast.name.
      toLowerCase().
      replace(/[^a-z0-9]+/g, '-').
      replace(/--+/g, '-').
      replace(/(^-)|(-$)/g, '');
    },

    podcastUrl() {
      const lstation = this.podcast.station ? this.podcast.station.toLowerCase() : '';
      return `${this.apiPrefix}/${lstation}`;
    },

    playerUrl() {
      // Omit ?smc= when there's no real smc (e.g. a zoneless embed) — a
      // literal "?smc=undefined" 500'd the backend share page.
      return buildPlayerUrl(this.podcastUrl, this.slug, this.podcast.id, this.smc);
    },

    // The API maps a podcast's raw category ids against the station's resolved
    // category list; an id with no match yields `null` (app.vue loadData). Vue1
    // silently rendered the resulting null entries as blank <li>s; Vue3's
    // stricter template eval throws on `null.id`/`null.name`, so filter the
    // unresolved entries out here. The REAL category tags (name/link) are the
    // same as the baseline; the corrected output is cleaner-than-identical —
    // the blank <li>s the baseline emitted for unresolved ids (each adding a
    // stray ", " via `ul.categories li:not(:last-child):after`, and, for an
    // ALL-null list, still showing the empty `ul.categories` + its tag-icon
    // `:before`) are dropped. Both the Webpack and Vite builds compile this
    // same source, so the two builds still match each other.
    itemCategories() {
      return (this.podcast.categories || []).filter(Boolean);
    },

  },

  // Vue1 `ready()` → Vue3 `mounted()`; `this.$els.activator` → `this.$refs.activator`.
  mounted() {
    // A shared episode auto-opens its inline player by synthesizing a click on
    // its (radio) activator. In popup mode (`next=popup`) there is no activator
    // (the button renders instead), so guard the ref — Vue1 would have thrown a
    // TypeError on `undefined.dispatchEvent` for a shared+popup embed.
    if (this.podcast.shared && this.$refs.activator) {
      const event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
      });
      this.$refs.activator.dispatchEvent(event);
    }
  },
  beforeUnmount() {
    // LP-4/MR-78 (fidelity fold, closes PS-12): the Vue-1 original had no
    // teardown, so a swapped-out / torn-down item left the <audio> playing and
    // any live hls.js session attached. Mirror the v4 Player sibling — pause
    // playback, then detach the live engine. detachLiveStream is null-safe +
    // throw-safe (handles an absent $refs.player and the iOS quirk).
    this.pauseMedia();
    detachLiveStream(this.$refs.player);
  },
};
</script>

<style lang="less" scoped>
* {box-sizing: border-box; margin: 0; padding: 0;}

.faded-background(@color) { background: @color; background: linear-gradient(@color, fade(@color, 50%)); }

@keyframes show-player {
  0% { opacity: 0; display: block; bottom: 42px; }
  100% { opacity: 1; bottom: 0;}
}

@keyframes hilight-shared {
  0% { background-color: yellow; }
  100% { background-color: transparent; }
}

.item {
  position: relative;
  padding: 0;
  margin: 0;
  border-top: #dddddd 1px solid;

  &.shared {
    animation: hilight-shared 2s ease 1;
  }

  button.open-futuri-media { display: none; }

  input[type=radio].play-futuri-media { display: none; }

  label.show-futuri-player {
    display: block;
    position: relative;
    font-size: 0;

    .media-player-container {
      border-radius: 10px;
      margin: 24px 0 12px 0;
      overflow: hidden;
      position: relative;

      .item-image {
        width: 100%;
        height: auto;
        max-width: 700px;
        cursor: pointer;

        // 🔑 RESERVE A BOX BEFORE THE BYTES ARRIVE. `width:100%; height:auto` with no
        // aspect-ratio makes this element ZERO HIGH until the image loads and then jump to
        // its full rendered height — pushing the `h2` and `p.description` below it down.
        // MEASURED in Faro RUM on postv4 over 13h (2026-08-20): 540 of the ~576 samples with
        // CLS > 0.1 land inside `div.items>div.item*`, the single biggest shift target being
        // the title `h2` (267 samples), at 0.9s-4.1s after navigation start — exactly when
        // widget.json resolves and rows are inserted. CLS p75 excluding ruralradio.com is
        // 0.233 against a 0.1 "good" threshold.
        //
        // 🪤 THE RATIO IS THE MEASURED MODE, NOT THE RIGHT ANSWER, and it cannot be: the
        // correct box needs each image's real dimensions, which the widget cannot get — the
        // URLs are opaque hashes, widget.json carries no width/height, and the
        // `square_image` field is not reliably square (measured one at 4032x3024, the same
        // dimensions as its own `image`). So this is a BOUNDED MITIGATION. The real fix is
        // meta_api returning dimensions so this `<img>` can carry `width`/`height`.
        //
        // 🔴 THE JUSTIFYING NUMBERS WERE WRONG THREE WAYS, and the corrected framing is
        // STRONGER than the one it replaces, so there is no reason to keep the old one:
        //
        //   1. "~76% of items shift by ~nothing" does not survive contact with the data.
        //      What is actually within 1% of 16/9 is ~34%; 48% of artwork is TALLER than
        //      the reservation.
        //   2. The distribution came from an UNNAMED ~2h window (it reproduces exactly for
        //      the 2h ending ~17:47Z). Over 12h55m the two modes are 33.0% / 16.5%, not
        //      40.6% / 35.7%.
        //   3. h/w 0.67 (3:2) IS THE #2 DESKTOP MODE AT 21.2% and was missing from both
        //      lists. It keeps a ~73px residual. ~97% of it is one tenant, which is a
        //      legitimate exclusion — but the old comment neither made it nor said under
        //      what population it was reproducing.
        //
        // THE CLAIM THAT SURVIVES ANY REWEIGHTING, and the one to cite: 16/9 removes
        // 88-90% of AGGREGATE shift (v1 desktop mean 435px -> 52px), 37.7% of items lose
        // >=99% of their shift, 93.6% lose >=75%, and 16/9 is within 0.2px of the
        // L1-optimal reservation. Those are properties of the whole distribution rather
        // than of whichever mode happened to be biggest in a two-hour window.
        //
        // `contain` covers the one frame between decode and Vue applying `art-settled`: it
        // letterboxes there rather than squashing a 4:3 image into 16/9. It is inert once
        // settled (aspect-ratio:auto + height:auto makes the box the intrinsic ratio, so
        // there is nothing to fit), and the max-width:480px block below re-declares
        // `cover` at equal specificity but LATER in source order, so mobile still crops.
        //
        // Browsers without `aspect-ratio` ignore both and get exactly today's behaviour, so
        // this degrades to the status quo rather than to something worse.
        aspect-ratio: 16 / 9;
        object-fit: contain;

        // #399: the artwork is width:100% cover; on a narrow column it otherwise renders as tall as the
        // column is wide (~324px at a 360px phone) — it dominates the mobile
        // viewport and runs well past the fold in landscape. Cap its height on
        // narrow viewports and center-crop (object-fit:cover) so it stays
        // FULL-WIDTH (keeping the top-right play `::after` aligned to the visible
        // art) without towering over the controls + title. 240px is a tunable
        // default (look pending sign-off). @media(max-width)-gated ONLY, so the
        // desktop (1280px) cross-cutover oracle is untouched.
        // NB the vertical variant is an exception — see src/v4-components/Player.vue.
        @media (max-width: 480px) {
          max-height: 240px;
          object-fit: cover;
        }
      }

      .item-image-placeholder {
        // Already reserves its own box, which is exactly why the no-image branch never
        // shifted while the image branch did. Deliberately gets NO aspect-ratio — a second
        // reservation on the same element would fight this height.
        height: 200px;
        .faded-background(lightgrey);
      }

      // 🔴 S12 — THE RESERVATION BUYS CLS AND SPENDS THE FOLD, AND THE ~40 LINES ABOVE
      // NEVER SAID SO. This paints the reserved box while it is empty, so the trade is at
      // least legible to the visitor.
      //
      // MEASURED, real builds, covers in flight: at 844x390 the six `h2` tops land at
      // 406/689/689/981/981/1272 — **0 of 6 titles visible** — where an `origin/development`
      // build at the same instant shows all six; at 390x844 it is 2-3 of 6. RUM puts cover
      // arrival 0.9-4.1s after navigation start, so a v1 phone embed shows a column of empty
      // rectangles for ~1-3s of every load. That is a REAL COST of the 16/9 reservation, not
      // a rendering artefact, and it is the honest other half of "CLS p75 0.233 -> ~0".
      //
      // A grey box reads as loading; an empty one reads as broken. Same mixin the no-image
      // placeholder already uses, so the two loading-ish states look like one design.
      //
      // 🪤 SCOPED TO THE UNSETTLED STATE, not painted-then-reset. A background on the base
      // `.item-image` rule would sit BEHIND every transparent PNG cover forever, and the
      // release rule below deliberately touches `aspect-ratio` only — adding a background
      // reset there would out-specify the mobile `cover` block by the same accident that
      // note warns about. `:not(.art-settled)` disappears on its own when the class lands.
      &:not(.art-settled) .item-image {
        .faded-background(lightgrey);
      }

      // 🔑 THE RELEASE. Once the art has loaded, its intrinsic ratio governs and the
      // reservation must get out of the way, or a non-16/9 image would be letterboxed
      // forever. Specificity (0,3,0) beats the base `.item-image` rule (0,2,0).
      //
      // 🪤 aspect-ratio ONLY — deliberately NOT object-fit. Resetting object-fit here would
      // also out-specify the max-width:480px `cover` rule and silently un-crop mobile.
      &.art-settled .item-image {
        aspect-ratio: auto;
      }
    }
    h2 {
      font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
      font-weight: normal;
      font-size: 16px;
      padding: 0;
      margin: 0 0 6px 0;
      cursor: pointer;
    }

    p.description {
      font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
      font-weight: normal;
      font-size: 12px;
      color: grey;
      margin: 0 0 5px 0;
      padding: 0;
      cursor: pointer;
    }

    &:after {
      content: "";
      position: absolute;
      top: 12px;
      right: 16px;
      display: inline-block;
      width: 30px;
      height: 30px;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.4);
      cursor: pointer;
      background-image: url(../assets/play-circle.svg);
      background-size: contain;
      border-radius: 15px;
      background-color: rgba(0,0,0,.4)
    }

    &:hover {
      h2 { opacity: .6; }
      p.description { opacity: .6; }
      &:after { opacity: .6; }
    }
  }

  input[type=radio].play-futuri-media:checked + label.show-futuri-player {
    .media-player-container {
      .item-image {  cursor: default; }
      .media-player { display: block; animation: show-player .5s ease 1; }
    }
    &:after { display: none; }
    &:hover {
      h2 { opacity: 1; cursor: default; }
      p.description { opacity: 1; cursor: default; }
      &:after { opacity: 1; cursor: default; }
    }
  }
  ul.categories {
    font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
    font-weight: normal;
    font-size: 0;
    list-style: none;
    margin: 12px 0 24px 0;
    padding: 0;

    &:before {
      content: "";
      display: inline-block;
      margin-right: 6px;
      background-image: url(../assets/tag.svg);
      background-size: cover;
      height: 12px;
      width: 12px;
    }
    li {
      display: inline-block;
      font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
      font-weight: normal;
      font-size: 12px;
      a {
        color: grey;
        cursor: pointer;
        &:hover { opacity: .6; }
      }
      &:not(:last-child) {
        &:after {
          content: ", ";
          margin-left: 0px;
        }
      }
    }
  }
  &:first-child { border-top: none;
    label.show-futuri-player {
      .media-player-container { margin-top: 0; }
    }
  }
}

.item.wide-view { // happens when wideView is true
  width: ~"calc(50% - 6px)";
  display: inline-block;
  vertical-align: top;
  margin: 0 12px 0 0;
  &:nth-child(odd) { margin: 0 0 0 0; }
  &:first-child { width: 100%; margin: 0 0 0 0;
    label.show-futuri-player {
      .media-player-container { border-radius: 10px; }
      h2 { font-size: 24px; }
      &:after { top: 24px; right: 24px; width: 60px; height: 60px; border-radius: 30px; }
    }
  }
}
.dark-theme .item {
  label.show-futuri-player {
    h2 {
      color: white;
    }
    p.description {
      color: lightgrey;
    }
  }
  ul.categories li a {
    color: lightgrey;
  }
}

.video-player {
  height: 0;
}
</style>
